import { AppPlatform } from "app/types";

/**
 * アプリ内の「Apple でサインイン」ボタン（ネイティブへ postMessage する自前のもの）を
 * 出すかを判定する。
 *
 * ⚠️ Apple サインインが使えるかの判定ではない。ブラウザは Apple 公式の
 *    `id="appleid-signin"` ボタン（AppleID.auth）を使うので、ここが false でも
 *    SSO.tsx の次の分岐でそちらが出る。
 *
 * - iOS: 常に true（ネイティブ API が最初からある）
 * - Android: buildVersion 38 以上。ブラウザ経由の Web フローを載せた版から
 * - Amazon: false（未対応）
 * - ブラウザ: false（アプリ内ではないため。Apple 公式ボタンへ回る）
 */
export const isNativeAppleSignInSupported = (): boolean => {
  if (!window.ReactNativeWebView) return false;

  const platform = window.parentsAppPlatform;
  if (platform === AppPlatform.iOS) {
    return true;
  }
  if (platform === AppPlatform.Android) {
    return Number(window.parentsBuildVersion) >= 38;
  }
  return false;
};

/**
 * index.html で async で読み込む外部の SDK の script が読み込まれるのを待つ。
 * 読み込み済みならすぐ返る。読み込みに失敗しても reject しないので、
 * 呼んだ側でグローバル（AppleID・google）があるかを確かめてから使う。
 */
const waitForScript = (src: string, isLoaded: () => boolean): Promise<void> =>
  new Promise((resolve) => {
    if (isLoaded()) {
      resolve();
      return;
    }
    const script = document.querySelector<HTMLScriptElement>(
      `script[src*="${src}"]`,
    );
    if (!script) {
      resolve();
      return;
    }
    script.addEventListener("load", () => resolve(), { once: true });
    script.addEventListener("error", () => resolve(), { once: true });
  });

export const isAppleIDSdkLoaded = (): boolean => "AppleID" in window;

export const waitForAppleIDSdk = (): Promise<void> =>
  waitForScript("appleid.auth.js", isAppleIDSdkLoaded);

export const isGoogleSdkLoaded = (): boolean => "google" in window;

export const waitForGoogleSdk = (): Promise<void> =>
  waitForScript("accounts.google.com/gsi/client", isGoogleSdkLoaded);
